import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Bot, CalendarCheck, CalendarClock, CalendarDays, CalendarRange, Check, ChevronDown,
  ChevronRight, Clock, FileText, Hash, Loader2, Mail, Mic, Play, Plus, Send, Timer, Trash2, UserRound,
  Wrench, X, Zap,
} from 'lucide-react';
import {
  AUTOMATION_TRIGGERS,
  DRIVE_SHARE_MODES,
  SLACK_DELIVERY_FORMATS,
  slackFormatProblem,
  WEEKDAYS,
  automationTriggerDefinition,
  describeGranolaFilter,
  describeSchedule,
  parseCalendarFilter,
  parseGranolaFilter,
  serializeCalendarFilter,
  serializeGranolaFilter,
  isScheduleTrigger,
  nextOccurrence,
  validateSchedule,
  type AutomationSchedule,
  type AutomationTrigger,
  type DriveShareMode,
  type SlackDeliveryFormat,
} from '@repo/shared-types';
import {
  AutomationFilterBar,
  DEFAULT_FILTERS,
  filtersFromParams,
  matchesRest,
  matchesShow,
  SHOW_OPTIONS,
  writeFilterParams,
  type AutomationFilterState,
  type ShowFilter,
} from '../components/automation/AutomationFilters';
import { DocEditTrend, DocVersionList } from '../components/automation/DocEdits';
import { Layout } from '../components/layout/Layout';
import { RunPhaseHeader, RunTimelineView } from '../components/run/RunActivity';
import { RunCostChip, RunCostStats, runUsageOf } from '../components/run/RunCost';
import { Badge } from '../components/ui/badge';
import { BreathingDot, SweepSurface } from '../components/ui/breathing';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Drawer } from '../components/ui/drawer';
import { Input, Select, Textarea } from '../components/ui/input';
import { Markdown } from '../components/ui/markdown';
import { Toggle } from '../components/ui/toggle';
import {
  api,
  type Automation,
  type AutomationInput,
  type AutomationRunDetail,
  type AutomationReadScope,
  type AutomationRunSummary,
  type AutomationRunStatus,
  type AutomationTemplate,
  type AutomationWithRuns,
  type GoogleStatus,
  type ListedAutomation,
  type CalendarMeetingSeries,
  type SlackChannelDetail,
  type SlackUserDetail,
} from '../lib/api';
import { useProjectTree } from '../lib/project-tree';
import { useLiveHeadline, useRunStream } from '../lib/run-stream';
import { cn, formatDate, timeAgo } from '../lib/utils';

/** A run in one of these is still moving, so the view polls until it isn't. */
const LIVE_STATUSES: AutomationRunStatus[] = ['QUEUED', 'PROCESSING'];

const POLL_MS = 3_000;

/**
 * A short list rather than every IANA zone: `Intl.supportedValuesOf('timeZone')` is 400+ entries,
 * which is a worse picker than a dozen useful ones plus the browser's own zone.
 */
const TIMEZONES = [
  'UTC',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Istanbul',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function statusVariant(status: AutomationRunStatus) {
  switch (status) {
    case 'COMPLETED':
      return 'success' as const;
    case 'FAILED':
      return 'danger' as const;
    case 'PROCESSING':
      return 'info' as const;
    default:
      return 'muted' as const;
  }
}

/** Exported so the project overview can label an automation the same way this page does. */
export function triggerIcon(trigger: AutomationTrigger) {
  switch (trigger) {
    case 'DAILY_AT_TIME':
      return CalendarClock;
    case 'WEEKLY_ON_DAYS':
      return CalendarDays;
    case 'MONTHLY_ON_DATE':
      return CalendarRange;
    case 'ONCE_AT_DATETIME':
      return CalendarCheck;
    case 'GRANOLA_NOTE_RECEIVED':
      return Mic;
    case 'SLACK_MESSAGE_RECEIVED':
      return Hash;
    case 'KB_RUN_COMPLETED':
      return Zap;
    case 'CALENDAR_EVENT_UPCOMING':
      return Clock;
    case 'CALENDAR_EVENT_ENDED':
      return Timer;
    case 'EMAIL_RECEIVED':
      return Mail;
    case 'DRIVE_FILE_RECEIVED':
      return FileText;
  }
}

/**
 * The scheduling fields of anything shaped like an automation — a saved row or an unsaved draft.
 *
 * A draft's optional fields become the row's nullable ones, which is the only difference between
 * the two as far as the schedule arithmetic is concerned.
 */
function scheduleOf(source: {
  trigger: AutomationTrigger;
  timeOfDay?: string | null;
  timezone?: string | null;
  daysOfWeek?: number[] | null;
  dayOfMonth?: number | null;
  runDate?: string | null;
}): AutomationSchedule {
  return {
    trigger: source.trigger,
    timeOfDay: source.timeOfDay ?? null,
    timezone: source.timezone || 'UTC',
    daysOfWeek: source.daysOfWeek ?? [],
    dayOfMonth: source.dayOfMonth ?? null,
    runDate: source.runDate ?? null,
  };
}

/**
 * "Every Monday and Thursday at 08:00 America/New_York", "New Granola meeting note in folder
 * abc123".
 *
 * Exported for the project overview, so one automation is described identically wherever it
 * appears — a schedule stated two different ways reads as two different schedules. The scheduled
 * kinds defer to `describeSchedule`, which the editor's preview also uses, for the same reason.
 */
export function triggerSummary(automation: Automation): string {
  switch (automation.trigger) {
    case 'GRANOLA_NOTE_RECEIVED':
    {
      const described = describeGranolaFilter(parseGranolaFilter(automation.sourceFilter));
      return described ? `New Granola note (${described})` : 'New Granola note in any folder';
    }
    case 'SLACK_MESSAGE_RECEIVED':
      return automation.sourceFilter
        ? `New Slack message in ${automation.sourceFilter}`
        : 'New Slack message in any linked channel';
    case 'KB_RUN_COMPLETED':
      return automation.sourceFilter
        ? `Ingestion run finished (${automation.sourceFilter})`
        : 'Ingestion run finished';
    case 'CALENDAR_EVENT_UPCOMING':
      return automation.sourceFilter
        ? `Before a meeting starts (${automation.sourceFilter})`
        : 'Before a meeting starts (30 minutes)';
    case 'CALENDAR_EVENT_ENDED':
      return automation.sourceFilter
        ? `After a meeting ends (${automation.sourceFilter})`
        : 'After a meeting ends (15 minutes)';
    default:
      return describeSchedule(scheduleOf(automation));
  }
}

/**
 * When a scheduled automation next fires, or null if it has no next fire — an event trigger, or a
 * one-shot whose moment has gone.
 *
 * Shown beside the schedule rather than instead of it: "every month on the 31st" and "next run
 * 30 Apr" answer different questions, and the second is the one that catches a mistake.
 */
export function nextRunSummary(automation: Automation): string | null {
  if (!isScheduleTrigger(automation.trigger)) return null;
  const at = nextOccurrence(scheduleOf(automation));
  return at ? formatDate(at) : null;
}

/** "June 2026" — the age of an automation is a month, not a timestamp. */
function monthYear(date: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(new Date(date));
}

/**
 * Three numbers about the automation as a whole, above its run rows.
 *
 * The rows below each answer "what happened in that run"; these answer "has this thing been
 * working", which no single row can — a list of three green runs looks identical whether it fired
 * weekly since June or three times last Tuesday.
 */
function AutomationStats({
  automation,
  lastRun,
}: {
  automation: AutomationWithRuns;
  lastRun?: AutomationRunSummary;
}) {
  const ran =
    automation.runCount === 0 ? 'Never' : automation.runCount === 1 ? 'Once' : `${automation.runCount} times`;

  const stats: { label: string; value: string; title?: string }[] = [
    { label: 'Ran', value: ran },
    {
      label: 'Last ran',
      // The exact moment, not `timeAgo`: "2d" is the right density for the collapsed row above,
      // but this panel is where someone checks whether Monday's 8am actually happened.
      value: lastRun ? formatDate(lastRun.queuedAt) : '—',
      title: lastRun ? `${lastRun.status.toLowerCase()} · ${timeAgo(lastRun.queuedAt)} ago` : undefined,
    },
    { label: 'Since', value: monthYear(automation.createdAt), title: formatDate(automation.createdAt) },
  ];

  return (
    <div className="grid grid-cols-3 divide-x divide-border-subtle">
      {stats.map((stat) => (
        <div key={stat.label} className="px-4 py-2.5" title={stat.title}>
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
            {stat.label}
          </span>
          <span className="block mt-0.5 text-xs text-ink-secondary truncate">{stat.value}</span>
        </div>
      ))}
    </div>
  );
}

/** The chosen mode's own description, so the consequence of widening access is stated in place. */
function driveShareHint(mode: DriveShareMode): string {
  return DRIVE_SHARE_MODES.find((m) => m.value === mode)?.description ?? '';
}

/** Today, in the browser's own zone — the sensible floor and default for a one-shot's date. */
function todayLocalIso(): string {
  const now = new Date();
  return new Intl.DateTimeFormat('en-CA', { timeZone: browserTimezone() }).format(now);
}

function emptyDraft(): AutomationInput {
  return {
    name: '',
    description: '',
    prompt: '',
    trigger: 'DAILY_AT_TIME',
    timeOfDay: '08:00',
    timezone: browserTimezone(),
    // Prefilled for every kind, not just the selected one, so switching the trigger lands on a
    // valid schedule rather than an empty form the Save button silently refuses.
    daysOfWeek: [1],
    dayOfMonth: 1,
    runDate: todayLocalIso(),
    sourceFilter: '',
    maxSteps: null,
    readScope: 'CONE',
    postToSlack: false,
    slackDestinations: [],
    saveToDrive: false,
    driveFolderId: '',
    driveShareMode: 'INHERIT',
    slackFormat: 'FULL',
    enabled: true,
  };
}

function draftFrom(automation: Automation): AutomationInput {
  return {
    name: automation.name,
    description: automation.description ?? '',
    prompt: automation.prompt,
    trigger: automation.trigger,
    timeOfDay: automation.timeOfDay ?? '08:00',
    timezone: automation.timezone,
    daysOfWeek: automation.daysOfWeek.length > 0 ? automation.daysOfWeek : [1],
    dayOfMonth: automation.dayOfMonth ?? 1,
    runDate: automation.runDate ?? todayLocalIso(),
    sourceFilter: automation.sourceFilter ?? '',
    maxSteps: automation.maxSteps,
    readScope: automation.readScope,
    postToSlack: automation.postToSlack,
    slackDestinations: automation.slackDestinations ?? [],
    saveToDrive: automation.saveToDrive,
    driveFolderId: automation.driveFolderId ?? '',
    driveShareMode: automation.driveShareMode,
    slackFormat: automation.slackFormat ?? 'FULL',
    enabled: automation.enabled,
  };
}

/** Spelled out per option because "scope" alone doesn't say which direction the tree is read in. */
const READ_SCOPE_HINTS: Record<AutomationReadScope, string> = {
  CONE: 'Reads organization-wide knowledge as well as this project and everything nested under it.',
  SUBTREE: 'Reads this project and every sub-project beneath it, but not the organization above.',
  SELF: "Reads only this project's own knowledge base.",
};

// ─── Editor ─────────────────────────────────────────────────────────────────

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-1.5">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-[11px] text-ink-faint leading-snug">{hint}</span>}
    </label>
  );
}

/** The Checkbox primitive is a bare styled input; these two need a clickable label beside them. */
function CheckboxField({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-xs text-ink-secondary">{label}</span>
    </label>
  );
}

/** Add or remove one id, order preserved — the list is what gets delivered to, in order. */
function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/**
 * One labelled block of destinations — channels, or people.
 *
 * Grouped because the two are picked for different reasons and a flat list of forty names with
 * three channels somewhere in it is unusable. Checkboxes rather than a multiple `<select>`: the
 * native control needs ctrl-click to add a second item and silently replaces the whole selection
 * otherwise, which is the interaction somebody choosing three recipients gets wrong every time.
 */
function DestinationGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: Array<{ id: string; label: string; detail?: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div>
      {/* `bg-surface-muted` and not a made-up `surface-2`: an undefined colour class generates no
          rule at all, so the header had no background and the rows scrolled visibly through it.
          `z-10` because a sticky element with no stacking order can still be painted under the
          siblings that scroll past it. */}
      <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-muted bg-surface-muted sticky top-0 z-10">
        {label}
      </p>
      {options.map((option) => (
        <label
          key={option.id}
          className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-surface-subtle"
        >
          <input
            type="checkbox"
            checked={selected.includes(option.id)}
            onChange={() => onToggle(option.id)}
            className="shrink-0"
          />
          <span className="truncate">{option.label}</span>
          {option.detail && <span className="text-ink-muted truncate">{option.detail}</span>}
        </label>
      ))}
    </div>
  );
}


/** The two triggers that fire off a meeting rather than a clock or an arrival. */
function isCalendarTrigger(trigger: AutomationTrigger): boolean {
  return trigger === 'CALENDAR_EVENT_UPCOMING' || trigger === 'CALENDAR_EVENT_ENDED';
}

/**
 * How long before or after — the values somebody actually wants, not a free number.
 *
 * A brief is useless if it lands while the meeting is starting and stale if it lands the night
 * before, and the useful band is narrow enough to enumerate. Typing a number invites 0 and 240,
 * both of which are mistakes this list cannot express.
 */
const CALENDAR_LEADS = [5, 10, 15, 30, 45, 60, 120] as const;

/**
 * The trigger's settings, built from what is on the calendar.
 *
 * It writes the *same* `sourceFilter` string the free-text path uses (`lead=30,title=Board Weekly`)
 * and parses that string back into these controls, so the two producers cannot disagree and editing
 * an automation without touching this panel is a no-op. `serializeCalendarFilter` is the inverse of
 * the dispatcher's own parser, which is what makes that true rather than hoped for.
 */
function CalendarTriggerFields({
  trigger,
  value,
  meetings,
  onChange,
}: {
  trigger: AutomationTrigger;
  value: string;
  meetings: CalendarMeetingSeries[];
  onChange: (next: string) => void;
}) {
  const edge = trigger === 'CALENDAR_EVENT_UPCOMING' ? 'START' : 'END';
  const filter = parseCalendarFilter(value, edge);
  const update = (patch: Partial<typeof filter>) =>
    onChange(serializeCalendarFilter({ ...filter, ...patch }));

  // A stored title that matches nothing on the calendar is kept as its own option rather than
  // silently resetting to "any meeting" — the meeting may simply be further out than this listing
  // reaches, and quietly widening a trigger to every meeting is the worst possible correction.
  const known = meetings.some((m) => m.title === filter.titleContains);

  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label={edge === 'START' ? 'How long before' : 'How long after'}>
        <Select
          value={String(filter.leadMinutes)}
          onChange={(e) => update({ leadMinutes: Number(e.target.value) })}
        >
          {CALENDAR_LEADS.map((minutes) => (
            <option key={minutes} value={minutes}>
              {minutes < 60 ? `${minutes} minutes` : `${minutes / 60} hour${minutes === 60 ? '' : 's'}`}
              {edge === 'START' ? ' before it starts' : ' after it ends'}
            </option>
          ))}
          {!CALENDAR_LEADS.includes(filter.leadMinutes as (typeof CALENDAR_LEADS)[number]) && (
            <option value={filter.leadMinutes}>{filter.leadMinutes} minutes</option>
          )}
        </Select>
      </Field>

      <Field
        label="Which meeting"
        hint={
          meetings.length === 0
            ? 'No upcoming meetings — no calendar is connected yet, or none is watched.'
            : filter.titleContains
              ? 'Matched by name, so every occurrence of this meeting fires.'
              : 'Every meeting on every watched calendar — usually far more than you want.'
        }
      >
        <Select
          value={filter.titleContains ?? ''}
          onChange={(e) => update({ titleContains: e.target.value || undefined })}
        >
          <option value="">Any meeting</option>
          {!known && filter.titleContains && (
            <option value={filter.titleContains}>{filter.titleContains} (not on the calendar now)</option>
          )}
          {meetings.map((m) => (
            <option key={`${m.calendarId}:${m.title}`} value={m.title}>
              {m.title}
              {m.occurrences > 1 ? ` · ${m.occurrences} upcoming` : ` · ${formatDate(m.nextStartsAt)}`}
              {/* Said out loud because it is the difference between one brief and several: the
                  meeting is on more than one watched calendar, and the trigger treats those as one
                  meeting rather than firing per attendee. Without this the row looks identical to
                  a meeting only one person has. */}
              {m.calendarCount > 1 ? ` · shared by ${m.calendarCount} calendars` : ''}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

type GranolaTitleRule = { mode: 'contains' | 'is'; text: string };

/** `people` is the field's raw text — addresses and `*` patterns, comma-separated — kept as typed. */
type GranolaFields = { folderId: string; rules: GranolaTitleRule[]; people: string };

function granolaRulesOf(value: string): GranolaFields {
  const filter = parseGranolaFilter(value);
  return {
    folderId: filter.folderId ?? '',
    rules: [
      ...filter.titleContains.map((text) => ({ mode: 'contains' as const, text })),
      ...filter.titleEquals.map((text) => ({ mode: 'is' as const, text })),
    ],
    people: filter.people.join(', '),
  };
}

function serializeGranolaRules({ folderId, rules, people }: GranolaFields): string {
  return serializeGranolaFilter({
    folderId: folderId.trim() || undefined,
    titleContains: rules.filter((r) => r.mode === 'contains').map((r) => r.text),
    titleEquals: rules.filter((r) => r.mode === 'is').map((r) => r.text),
    people: people.split(',').map((p) => p.trim()).filter(Boolean),
  });
}

/**
 * The Granola trigger's settings: an optional folder, any number of meeting-title rules, and the
 * people at least one of whom must have been in the meeting.
 *
 * Writes the same `folder=…,title=…,title_is=…,person=…` string an MCP client or a hand-typed filter would,
 * through `serializeGranolaFilter`, the inverse of the dispatcher's parser. Rows are held locally
 * because a rule whose text is still empty serializes to nothing, and would otherwise vanish the
 * moment it was added.
 */
function GranolaTriggerFields({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const [state, setState] = useState(() => granolaRulesOf(value));

  // Re-seed when the stored value changes from outside (another automation opened, a template).
  useEffect(() => {
    if (serializeGranolaRules(state) !== value) setState(granolaRulesOf(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (next: GranolaFields) => {
    setState(next);
    onChange(serializeGranolaRules(next));
  };
  const setRule = (index: number, patch: Partial<GranolaTitleRule>) =>
    update({ ...state, rules: state.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) });

  return (
    <div className="space-y-3">
      <Field
        label="Meeting title"
        hint={
          state.rules.length === 0
            ? 'Every synced note fires. Add a rule to fire only for certain meetings.'
            : 'Checked against the note title and its calendar event title, ignoring case. Any one rule matching is enough.'
        }
      >
        <div className="space-y-2">
          {state.rules.map((rule, index) => (
            <div key={index} className="flex items-center gap-2">
              <Select
                value={rule.mode}
                onChange={(e) => setRule(index, { mode: e.target.value as GranolaTitleRule['mode'] })}
                className="w-40 shrink-0"
              >
                <option value="contains">contains</option>
                <option value="is">is exactly</option>
              </Select>
              <Input
                value={rule.text}
                onChange={(e) => setRule(index, { text: e.target.value })}
                placeholder={rule.mode === 'contains' ? 'Board' : 'Board Weekly'}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => update({ ...state, rules: state.rules.filter((_, i) => i !== index) })}
                aria-label="Remove rule"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => update({ ...state, rules: [...state.rules, { mode: 'contains', text: '' }] })}
          >
            <Plus className="w-3.5 h-3.5" /> Add title rule
          </Button>
        </div>
      </Field>

      <Field
        label="People in the meeting"
        hint={
          state.people.trim()
            ? 'Fires only when one of these was in the meeting — the note’s owner, an attendee, or the invite’s organiser or an invitee.' +
              (state.rules.length > 0 ? ' A title rule must match as well.' : '')
            : 'Anyone. Add addresses, or a pattern like *@board.fun, to fire only when one of them was there.'
        }
      >
        <Input
          value={state.people}
          onChange={(e) => update({ ...state, people: e.target.value })}
          placeholder="anto@example.com, *@board.fun"
          className="font-mono text-xs"
        />
      </Field>

      <Field label="Only from folder" hint="A Granola folder id. Blank = any watched folder.">
        <Input
          value={state.folderId}
          onChange={(e) => update({ ...state, folderId: e.target.value })}
          placeholder="granola-folder-id"
          className="font-mono text-xs"
        />
      </Field>
    </div>
  );
}

function AutomationEditor({
  open,
  editing,
  projects,
  homeProjectId,
  onHomeProjectChange,
  templates,
  channels,
  meetings,
  slackUsers,
  google,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  editing: Automation | null;
  /** Every project, for choosing where this automation is homed. */
  projects: Array<{ id: string; name: string; depth: number }>;
  homeProjectId: string;
  onHomeProjectChange: (projectId: string) => void;
  templates: AutomationTemplate[];
  channels: SlackChannelDetail[];
  meetings: CalendarMeetingSeries[];
  slackUsers: SlackUserDetail[];
  /** The deployment's Google credential, for saying what a document will be filed as. */
  google: GoogleStatus | null;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: AutomationInput) => void;
}) {
  const [draft, setDraft] = useState<AutomationInput>(emptyDraft);

  /** Who a run will file as, or null when this deployment has no Google credential at all. */
  const driveIdentity = google?.mode === 'service-account' ? google.identity : null;

  // Reset on every open so a cancelled edit doesn't leak into the next one.
  useEffect(() => {
    if (open) setDraft(editing ? draftFrom(editing) : emptyDraft());
  }, [open, editing]);

  const definition = automationTriggerDefinition(draft.trigger);
  const set = <K extends keyof AutomationInput>(key: K, value: AutomationInput[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const applyTemplate = (id: string) => {
    const template = templates.find((t) => t.id === id);
    if (!template) return;
    setDraft((d) => ({
      ...d,
      // The name is only filled if it is still untouched — re-picking a template to refresh the
      // prompt should not silently rename an automation someone already named.
      name: d.name.trim() ? d.name : template.name,
      description: d.description?.trim() ? d.description : template.summary,
      prompt: template.prompt,
      trigger: template.suggestedTrigger,
    }));
  };

  // One validator for the whole schedule, shared with the API — so the button is disabled for
  // exactly the reasons the server would have rejected it, stated in the same words.
  const schedule = scheduleOf(draft);
  const scheduleError = validateSchedule(schedule);
  const nextRun = scheduleError ? null : nextOccurrence(schedule);
  // homeProjectId is part of the payload, so an empty one (tree still loading, or no projects)
  // would post an invalid create rather than being caught by the form.
  const canSave =
    draft.name.trim().length > 0 &&
    draft.prompt.trim().length > 0 &&
    homeProjectId.length > 0 &&
    !scheduleError &&
    !slackFormatProblem({ saveToDrive: draft.saveToDrive ?? false, slackFormat: draft.slackFormat ?? 'FULL' }) &&
    !saving;

  const toggleWeekday = (day: number) => {
    const current = draft.daysOfWeek ?? [];
    set(
      'daysOfWeek',
      current.includes(day) ? current.filter((d) => d !== day) : [...current, day].sort((a, b) => a - b),
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? 'Edit automation' : 'New automation'}
      className="max-w-2xl"
    >
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {error && (
          <div className="px-4 py-3 rounded-md bg-danger-bg border border-danger-ring text-sm text-danger-text">
            {error}
          </div>
        )}

        {!editing && templates.length > 0 && (
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-1.5">
              Start from a template
            </span>
            <div className="flex flex-wrap gap-2">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => applyTemplate(template.id)}
                  title={template.summary}
                  className="px-2.5 py-1 rounded-full border border-border bg-surface-subtle text-[11px] font-medium text-ink-secondary hover:border-orange-400 hover:text-orange-600 transition-colors"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <Field label="Name">
          <Input
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Board Weekly pre-meeting brief"
          />
        </Field>

        <Field label="Description" hint="Optional. One line, shown in the list.">
          <Input
            value={draft.description ?? ''}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Open threads and carried-over items before the Monday meeting"
          />
        </Field>

        <Field
          label="Prompt"
          hint="The whole configuration. Say what to gather and give the exact output format you want — the agent has the knowledge base, Granola notes, Slack messages and its own previous outputs to work from."
        >
          <Textarea
            value={draft.prompt}
            onChange={(e) => set('prompt', e.target.value)}
            rows={16}
            className="font-mono text-xs leading-relaxed"
            placeholder={'Produce a pre-meeting brief for…\n\nGather:\n1. …\n\nThen emit exactly this structure:\n\n# [Meeting Name], [Date]\n…'}
          />
        </Field>

        <Field label="Trigger" hint={definition.description}>
          <Select value={draft.trigger} onChange={(e) => set('trigger', e.target.value as AutomationTrigger)}>
            {AUTOMATION_TRIGGERS.map((t) => (
              <option key={t.trigger} value={t.trigger}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>

        {definition.needsTime && (
          <div className="space-y-3">
            {/* Which days the schedule lands on comes first, then the time it lands at — the same
                order the sentence under it reads in. */}
            {definition.needsDaysOfWeek && (
              <Field label="Days" hint="Runs at the same time on each day you pick.">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAYS.map((day) => {
                    const active = (draft.daysOfWeek ?? []).includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleWeekday(day.value)}
                        aria-pressed={active}
                        className={cn(
                          'px-2.5 py-1 rounded-md border text-[11px] font-medium transition-colors',
                          active
                            ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300'
                            : 'border-border bg-surface-subtle text-ink-secondary hover:border-orange-300',
                        )}
                      >
                        {day.short}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}

            {definition.needsDayOfMonth && (
              <Field
                label="Day of the month"
                hint="Months that are shorter than the date you pick run on their last day instead."
              >
                <Select
                  value={String(draft.dayOfMonth ?? 1)}
                  onChange={(e) => set('dayOfMonth', Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            {definition.needsRunDate && (
              <Field label="Date" hint="The single day this runs on. It does not repeat.">
                <Input
                  type="date"
                  value={draft.runDate ?? ''}
                  onChange={(e) => set('runDate', e.target.value)}
                />
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Time">
                <Input
                  type="time"
                  value={draft.timeOfDay ?? ''}
                  onChange={(e) => set('timeOfDay', e.target.value)}
                  className={scheduleError ? 'border-danger-ring' : undefined}
                />
              </Field>
              <Field label="Timezone">
                <Select value={draft.timezone} onChange={(e) => set('timezone', e.target.value)}>
                  {/* The browser's own zone first, and included even when it isn't in the list. */}
                  {[browserTimezone(), ...TIMEZONES.filter((z) => z !== browserTimezone())].map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            {/* The schedule read back as a sentence, plus the instant it resolves to. "The 31st of
                every month" and "every Monday" are both easy to configure and hard to picture, and
                a one-shot dated in the past will never fire at all — which is worth saying before
                someone waits for it rather than after. */}
            <div
              className={cn(
                'px-3 py-2 rounded-md border text-[11px] leading-relaxed',
                scheduleError
                  ? 'bg-danger-bg border-danger-ring text-danger-text'
                  : 'bg-surface-subtle border-border text-ink-muted',
              )}
            >
              {scheduleError ? (
                scheduleError
              ) : (
                <>
                  <span className="text-ink-secondary font-medium">{describeSchedule(schedule)}</span>
                  {nextRun ? (
                    <>
                      {' · '}Next run {formatDate(nextRun)}
                    </>
                  ) : (
                    <>{' · '}That time has already passed, so this will never run.</>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* The calendar triggers get a builder rather than the raw string. The grammar is the same
            either way — the controls below compose `lead=30,title=Board Weekly` and parse it back —
            so an automation written by hand, by MCP or by this editor is one shape. What the picker
            buys is that the meeting is *chosen* from what is actually on the calendar rather than
            typed and hoped to match. */}
        {isCalendarTrigger(draft.trigger) ? (
          <CalendarTriggerFields
            trigger={draft.trigger}
            value={draft.sourceFilter ?? ''}
            meetings={meetings}
            onChange={(next) => set('sourceFilter', next)}
          />
        ) : draft.trigger === 'GRANOLA_NOTE_RECEIVED' ? (
          <GranolaTriggerFields
            value={draft.sourceFilter ?? ''}
            onChange={(next) => set('sourceFilter', next)}
          />
        ) : (
          definition.sourceFilterLabel && (
            <Field label="Only from" hint={definition.sourceFilterLabel}>
              <Input
                value={draft.sourceFilter ?? ''}
                onChange={(e) => set('sourceFilter', e.target.value)}
                placeholder={
                  draft.trigger === 'SLACK_MESSAGE_RECEIVED'
                    ? 'C01234567'
                    : draft.trigger === 'KB_RUN_COMPLETED'
                      ? 'FILE_UPLOAD, GRANOLA, FAILED'
                      : 'granola-folder-id'
                }
                className="font-mono text-xs"
              />
            </Field>
          )
        )}

        {/* Where the automation lives. This is what "project" means for an automation now that
            they are managed centrally: the destination `kb_save_note` writes to, and the source of
            the Slack channels it may post to — not the set of things it can see, which is below. */}
        <Field
          label="Home project"
          hint="Where its output is filed, and whose linked Slack channels it may post to."
        >
          <Select value={homeProjectId} onChange={(e) => onHomeProjectChange(e.target.value)}>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {'  '.repeat(p.depth)}
                {p.name}
              </option>
            ))}
          </Select>
        </Field>

        {/* What the agent may *read*. Writing is unaffected: whatever is chosen here, the document
            a run produces is still filed against the home project above. */}
        <Field
          label="Knowledge it can read"
          hint={READ_SCOPE_HINTS[draft.readScope ?? 'CONE']}
        >
          <Select value={draft.readScope ?? 'CONE'} onChange={(e) => set('readScope', e.target.value as AutomationReadScope)}>
            <option value="CONE">This project, the organization, and sub-projects</option>
            <option value="SUBTREE">This project and its sub-projects</option>
            <option value="SELF">This project only</option>
          </Select>
        </Field>

        <div className="pt-2 border-t border-border-subtle space-y-3">
          <CheckboxField
            checked={draft.postToSlack ?? false}
            onChange={(next) => set('postToSlack', next)}
            label="Post the output to Slack as soon as the run finishes"
          />

          {draft.postToSlack && (
            <Field
              label="Send to"
              hint={
                channels.length === 0 && slackUsers.length === 0
                  ? 'Nothing available — the Slack bot token may not be configured.'
                  : (draft.slackDestinations?.length ?? 0) === 0
                    ? "Nothing picked — delivers to the project's first linked channel."
                    : `${draft.slackDestinations!.length} selected. Each gets the same message.`
              }
            >
              {/* A checkbox list rather than a multiple <select>: a native multi-select needs
                  ctrl-click to add a second item and silently replaces the selection otherwise,
                  which is exactly the interaction somebody picking three recipients gets wrong. */}
              <div className="max-h-56 overflow-y-auto rounded border border-border divide-y divide-border">
                {channels.length > 0 && (
                  <DestinationGroup
                    label="Channels"
                    options={channels.map((c) => ({ id: c.id, label: `#${c.name ?? c.id}` }))}
                    selected={draft.slackDestinations ?? []}
                    onToggle={(id) => set('slackDestinations', toggle(draft.slackDestinations ?? [], id))}
                  />
                )}
                {slackUsers.length > 0 && (
                  <DestinationGroup
                    label="Direct message"
                    options={slackUsers.map((u) => ({
                      id: u.id,
                      label: u.name,
                      detail: u.handle,
                    }))}
                    selected={draft.slackDestinations ?? []}
                    onToggle={(id) => set('slackDestinations', toggle(draft.slackDestinations ?? [], id))}
                  />
                )}
              </div>
            </Field>
          )}

          {draft.postToSlack && (
            <Field
              label="What Slack gets"
              hint={
                draft.saveToDrive
                  ? SLACK_DELIVERY_FORMATS.find((f) => f.value === (draft.slackFormat ?? 'FULL'))?.description
                  : 'A heads-up links the Google Doc, so it needs "Also file the output in Google Drive" below.'
              }
            >
              <Select
                value={draft.slackFormat ?? 'FULL'}
                onChange={(e) => set('slackFormat', e.target.value as SlackDeliveryFormat)}
              >
                {SLACK_DELIVERY_FORMATS.map((format) => (
                  <option key={format.value} value={format.value} disabled={format.value === 'HEADS_UP' && !draft.saveToDrive}>
                    {format.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <CheckboxField
            checked={draft.saveToDrive ?? false}
            onChange={(next) => {
              set('saveToDrive', next);
              // A heads-up has nothing to link once filing is off, so it goes back to the full report
              // rather than leaving a setting the Save button would then refuse.
              if (!next && draft.slackFormat === 'HEADS_UP') set('slackFormat', 'FULL');
            }}
            label="Also file the output in Google Drive as a Doc"
          />

          {draft.saveToDrive && driveIdentity === null && (
            <p className="text-[11px] text-warning-text bg-warning-bg border border-warning-ring rounded px-3 py-2 leading-snug">
              {/* Named rather than linked: this sits inside an unsaved draft, and navigating away to
                  connect would cost it. Connecting is a system-admin action, so for most people the
                  useful sentence is who to ask rather than where to click. */}
              This deployment has no Google credential configured, so runs will complete with the
              document undelivered to Drive. It is a server setting
              (<span className="font-mono">GOOGLE_SERVICE_ACCOUNT_KEY</span>), not something to
              connect here.
            </p>
          )}

          {draft.saveToDrive && (
            <>
              {/* Stated, not chosen. The deployment authenticates as one service account, so there
                  is no per-automation question of whose Drive this lands in — and no column left to
                  store an answer in. */}
              {driveIdentity && (
                <p className="text-[11px] text-ink-faint leading-snug">
                  Documents are created by <span className="text-ink-secondary">{driveIdentity}</span>,
                  this deployment's Google service account, in the folder below.
                </p>
              )}

              <Field
                label="Drive folder id"
                hint="From the folder URL: drive.google.com/drive/folders/<id>. Blank uses the server default."
              >
                <Input
                  value={draft.driveFolderId ?? ''}
                  onChange={(e) => set('driveFolderId', e.target.value)}
                  placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
                  className="font-mono text-xs"
                />
              </Field>

              <Field label="Who can open it" hint={driveShareHint(draft.driveShareMode ?? 'INHERIT')}>
                <Select
                  value={draft.driveShareMode ?? 'INHERIT'}
                  onChange={(e) => set('driveShareMode', e.target.value as DriveShareMode)}
                >
                  {DRIVE_SHARE_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </>
          )}

          <CheckboxField
            checked={draft.enabled ?? true}
            onChange={(next) => set('enabled', next)}
            label="Enabled — triggers fire while this is on"
          />
        </div>
      </div>

      <div className="flex-shrink-0 flex items-center justify-end gap-2 px-6 py-4 border-t border-border">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSave} onClick={() => onSave(draft)}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {editing ? 'Save changes' : 'Create automation'}
        </Button>
      </div>
    </Drawer>
  );
}

// ─── Run detail ─────────────────────────────────────────────────────────────

function StepTrail({ steps }: { steps: Array<{ tool: string; input: unknown; output: unknown }> }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  return (
    <div className="divide-y divide-border-subtle border border-border rounded-lg overflow-hidden">
      {steps.map((step, i) => (
        <div key={i}>
          <button
            onClick={() => setExpanded(expanded === i ? null : i)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-subtle transition-colors"
          >
            <span className="text-[10px] tabular-nums text-ink-faint w-5">{i + 1}</span>
            <Wrench size={11} className="text-ink-faint flex-shrink-0" />
            <span className="text-xs font-mono text-ink-secondary">{step.tool}</span>
            {expanded === i ? (
              <ChevronDown size={12} className="ml-auto text-ink-faint" />
            ) : (
              <ChevronRight size={12} className="ml-auto text-ink-faint" />
            )}
          </button>
          {expanded === i && (
            <div className="px-3 pb-3 space-y-2 bg-surface-subtle">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Input</p>
                <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words">
                  {JSON.stringify(step.input, null, 2)}
                </pre>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint mb-1">Output</p>
                <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap break-words">
                  {JSON.stringify(step.output, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function RunDrawer({
  open,
  run,
  loading,
  onClose,
}: {
  open: boolean;
  run: AutomationRunDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  const [showTrail, setShowTrail] = useState(false);
  const steps = run?.steps?.calls ?? [];

  /**
   * The live account of this run — the same hook, timeline and components the ingestion runs use.
   *
   * Keyed on the run's id, so opening a different run tears the stream down rather than leaving an
   * EventSource (and a Redis connection behind it) attached to a run nobody is looking at.
   */
  const { timeline, live, loading: timelineLoading, error: streamError } = useRunStream(
    run?.projectId ?? null,
    run?.id ?? null,
    run?.status ?? null,
    'automation',
  );
  const headline = useLiveHeadline(timeline);

  return (
    <Drawer open={open} onClose={onClose} title={run?.automation.name ?? 'Run'} className="max-w-3xl">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {loading && !run ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : !run ? (
          <p className="text-sm text-ink-faint">Run not found.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
              <Badge variant="default">{run.trigger}</Badge>
              {run.postedToSlack && (
                <Badge variant="info">
                  <Send size={9} className="mr-1" />
                  Posted to Slack
                </Badge>
              )}
              <span className="ml-auto text-xs text-ink-faint">{formatDate(run.queuedAt)}</span>
            </div>

            {/* First, not last: for a run in flight this is the only thing anyone opened the drawer
                for — the output is still empty and the counters are still zero. */}
            <div className="space-y-3">
              <RunPhaseHeader timeline={timeline} headline={headline} live={live} />
              <RunTimelineView timeline={timeline} live={live} loading={timelineLoading} />
              {streamError && (
                <p className="text-[11px] text-ink-faint">
                  {streamError} The record below is still complete up to where it stopped.
                </p>
              )}
            </div>

            <div className="grid grid-cols-5 gap-3 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint">Tool calls</p>
                <p className="text-ink tabular-nums">{run.toolCalls}</p>
              </div>
              {/* Includes what any `kb_ask` in the loop spent: that is a whole retrieval run made on
                  this automation's behalf, and charging it elsewhere would understate every
                  automation that asks the knowledge base a question. */}
              <RunCostStats usage={runUsageOf(run)} />
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint">Started</p>
                <p className="text-ink">{run.startedAt ? timeAgo(run.startedAt) : '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-ink-faint">Finished</p>
                <p className="text-ink">{run.finishedAt ? timeAgo(run.finishedAt) : '—'}</p>
              </div>
            </div>

            {run.errorMessage && (
              <div className="px-4 py-3 rounded-md bg-danger-bg border border-danger-ring text-sm text-danger-text">
                <AlertTriangle size={13} className="inline mr-1.5 -mt-0.5" />
                {run.errorMessage}
              </div>
            )}

            {run.deliveryError && (
              <div className="px-4 py-3 rounded-md bg-warning-bg border border-warning-ring text-sm text-warning-text">
                Output produced, but Slack delivery failed: {run.deliveryError}
              </div>
            )}

            {run.deliverySkipped && (
              <div className="px-4 py-3 rounded-md bg-surface-subtle border border-border-subtle text-sm text-ink-secondary">
                <span className="font-medium text-ink">Not delivered.</span> The agent kept this run’s output here
                rather than posting or filing it: {run.deliverySkipped}
              </div>
            )}

            {/* A run can be filed and unshared at once, so the link and the warning both show. */}
            {run.driveFileUrl && (
              <a
                href={run.driveFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-700 transition-colors"
              >
                <FileText size={12} />
                Open in Google Drive
              </a>
            )}

            {run.driveError && (
              <div className="px-4 py-3 rounded-md bg-warning-bg border border-warning-ring text-sm text-warning-text">
                Google Drive: {run.driveError}
              </div>
            )}

            <DocVersionList versions={run.docVersions ?? []} />

            {/* Input — what the agent was asked, as it stood at run time. */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2">Input</p>
              <Card className="px-4 py-3 space-y-2">
                {run.input?.triggerDescription && (
                  <p className="text-xs text-ink-muted">
                    Triggered by {run.input.triggerDescription}
                    {run.input.localTime ? ` · ${run.input.localTime}` : ''}
                  </p>
                )}
                <pre className="text-[11px] font-mono text-ink-secondary whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                  {run.input?.prompt ?? run.automation.prompt}
                </pre>
                {run.input?.triggerExcerpt && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-ink-faint mb-1 mt-2">Triggering item</p>
                    <pre className="text-[11px] font-mono text-ink-muted whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
                      {run.input.triggerExcerpt}
                    </pre>
                  </div>
                )}
              </Card>
            </div>

            {/* Output — the deliverable. */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint mb-2">Output</p>
              {run.output ? (
                <Card className="px-5 py-4">
                  <Markdown>{run.output}</Markdown>
                </Card>
              ) : LIVE_STATUSES.includes(run.status) ? (
                <div className="flex flex-col items-center gap-2.5 px-4 py-7 border border-dashed border-border rounded-lg">
                  <BreathingDot size={10} />
                  <p className="text-sm text-ink-muted">
                    {run.status === 'QUEUED' ? 'Queued — waiting for a worker' : headline.line || 'Working'}
                  </p>
                  <p className="text-[11px] text-ink-faint">
                    The document appears here once the agent has written it.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-ink-faint px-4 py-6 border border-dashed border-border rounded-lg text-center">
                  No output.
                </p>
              )}
            </div>

            {steps.length > 0 && (
              <div>
                <button
                  onClick={() => setShowTrail((s) => !s)}
                  className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint hover:text-ink-muted transition-colors mb-2"
                >
                  {showTrail ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Raw trace ({steps.length} tool calls)
                </button>
                {showTrail && <StepTrail steps={steps} />}
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

// ─── Run row ────────────────────────────────────────────────────────────────

function RunRow({ run, onOpen }: { run: AutomationRunSummary; onOpen: () => void }) {
  const live = LIVE_STATUSES.includes(run.status);
  return (
    <SweepSurface active={live}>
      <button
        onClick={onOpen}
        className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-surface-subtle transition-colors"
      >
        {/* A live run breathes instead of showing a status word that never changes: the row's job
            while a run is moving is to say it is alive and what it is doing. */}
        {live ? (
          <span className="inline-flex items-center gap-1.5">
            <BreathingDot size={7} />
            <span className="text-[11px] font-medium text-orange-600 dark:text-orange-400">
              {run.status === 'QUEUED' ? 'queued' : 'running'}
            </span>
          </span>
        ) : (
          <Badge variant={statusVariant(run.status)}>{run.status}</Badge>
        )}
        <span className="text-xs text-ink-muted">{run.trigger}</span>
        {/* `activity` is kept current on the row by the worker, so this line names the step the agent
            is on without the list opening a stream per row. */}
        {live && run.activity && (
          <span className="text-[11px] text-ink-muted truncate" title={run.activity}>
            {run.activity}
          </span>
        )}
        {run.postedToSlack && <Send size={10} className="text-ink-faint" />}
        {run.deliverySkipped && (
          <span className="text-[11px] text-ink-faint truncate" title={run.deliverySkipped}>
            not delivered
          </span>
        )}
        {run.errorMessage && <span className="text-xs text-danger-text truncate">{run.errorMessage}</span>}
        {/* One right-aligned group rather than two `ml-auto` siblings, which would share the free
            space between them and leave the cost floating mid-row when the chip renders. */}
        <span className="ml-auto flex items-center gap-2 flex-shrink-0">
          {/* An automation runs on a schedule, so its cost is the one that accumulates quietly — a
              daily brief at forty cents is a bill nobody chose. Read down the column, not per run. */}
          <RunCostChip usage={runUsageOf(run)} />
          <span className="text-xs text-ink-faint whitespace-nowrap">{timeAgo(run.queuedAt)}</span>
        </span>
      </button>
    </SweepSurface>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

const SHOW_STORAGE_KEY = 'automations.show';

/** The tab this browser last chose. Storage can be unavailable (private mode), so never throws. */
function readRememberedShow(): ShowFilter | null {
  try {
    const value = window.localStorage.getItem(SHOW_STORAGE_KEY);
    return SHOW_OPTIONS.some((o) => o.key === value) ? (value as ShowFilter) : null;
  } catch {
    return null;
  }
}

function rememberShow(show: ShowFilter): void {
  try {
    window.localStorage.setItem(SHOW_STORAGE_KEY, show);
  } catch {
    // A preference that cannot be kept is simply not kept.
  }
}

export default function Automations() {
  // Org-level: the whole set, not one project's. `homeProjectId` is the destination chosen in the
  // editor, which is what an automation is homed at — no longer implied by the URL.
  const { allProjects, organization } = useProjectTree();
  const [searchParams, setSearchParams] = useSearchParams();

  const [automations, setAutomations] = useState<ListedAutomation[]>([]);
  /** Which project the automation being edited is homed at — its write destination. */
  const [homeProjectId, setHomeProjectId] = useState('');
  const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
  const [channels, setChannels] = useState<SlackChannelDetail[]>([]);
  const [slackUsers, setSlackUsers] = useState<SlackUserDetail[]>([]);
  const [meetings, setMeetings] = useState<CalendarMeetingSeries[]>([]);
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Automation | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [runsFor, setRunsFor] = useState<Record<string, AutomationRunSummary[]>>({});

  /**
   * The run the drawer is showing, as id *and* home project.
   *
   * Both are needed to fetch it: run detail lives on the owning project's route, and a run
   * reached from this org-wide list may belong to any project. Held separately from `openRun`
   * because it has to survive that being cleared while the detail loads — deriving the project
   * from the loaded run is circular, and was why the drawer never populated.
   */
  const [openRunRef, setOpenRunRef] = useState<{ id: string; projectId: string } | null>(null);
  const [openRun, setOpenRun] = useState<AutomationRunDetail | null>(null);
  const [runLoading, setRunLoading] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Which automations are showing. In the URL, so a filtered view survives a reload and can be sent
   * to someone; `show` with no parameter falls back to the last choice this browser made, then to
   * "Mine" — or to "All" for someone who has none, rather than opening on an empty page.
   */
  const defaultShow: ShowFilter = useMemo(() => {
    const remembered = readRememberedShow();
    if (remembered) return remembered;
    return automations.some((a) => a.createdByMe || a.deliveredToMe) ? 'mine' : 'all';
  }, [automations]);
  const filters = useMemo(() => filtersFromParams(searchParams, defaultShow), [searchParams, defaultShow]);
  const setFilters = useCallback(
    (next: AutomationFilterState) => {
      setSearchParams(writeFilterParams(searchParams, next), { replace: true });
      rememberShow(next.show);
    },
    [searchParams, setSearchParams],
  );
  const visible = useMemo(
    () => automations.filter((a) => matchesShow(a, filters.show) && matchesRest(a, filters)),
    [automations, filters],
  );

  const load = useCallback(async () => {
    try {
      const list = await api.orgAutomations.list();
      setAutomations(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * `?automation=<id>` opens that one expanded — how a link from elsewhere (the project overview)
   * lands on a specific automation rather than on the top of an org-wide list.
   *
   * Consumed once and stripped from the URL, so collapsing the row by hand isn't undone by the
   * next render, and a reload doesn't re-open what someone just closed.
   */
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked) return;
    const wanted = searchParams.get('automation');
    if (!wanted) return;

    setDeepLinked(true);
    setExpanded(wanted);
    refreshRuns(wanted);

    const next = new URLSearchParams(searchParams);
    next.delete('automation');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinked, searchParams, setSearchParams]);

  /**
   * `?run=<id>&runProject=<projectId>` reopens that run's drawer — a pasted link, a reload, or a
   * link from the activity view landing on the run someone means rather than on the list.
   *
   * Unlike `?automation=`, this one is **not** stripped: it is what makes the URL in the address
   * bar worth copying while the drawer is open, and `closeRunDetail` is what removes it.
   *
   * `runProject` is optional on the way in, because a link written by hand usually has only the
   * run id. When it is missing the project is resolved from the run tails already loaded, which
   * covers any recent run; an older one cannot be resolved without an org-wide run endpoint that
   * does not exist, so the parameter is dropped rather than left pointing at a drawer that never
   * opens. Waits for `automations` for exactly that reason.
   */
  const [restoredRun, setRestoredRun] = useState(false);
  useEffect(() => {
    if (restoredRun || loading) return;
    const wantedRun = searchParams.get('run');
    if (!wantedRun) return;

    setRestoredRun(true);
    const wantedProject =
      searchParams.get('runProject') ??
      automations.flatMap((a) => a.runs).find((r) => r.id === wantedRun)?.projectId ??
      null;

    if (wantedProject) {
      openRunDetail({ id: wantedRun, projectId: wantedProject });
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('run');
    next.delete('runProject');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredRun, loading, automations, searchParams, setSearchParams]);

  useEffect(() => {
    api.orgAutomations.templates().then(setTemplates).catch(() => setTemplates([]));
    // Channels are only needed for the delivery picker, and the endpoint fails outright when no
    // bot token is configured — a missing picker is fine, a broken page is not.
    api.slack.listChannels().then(setChannels).catch(() => setChannels([]));
    // People, for the direct-message half of the delivery picker. Swallowed like channels: listing
    // them needs `users:read` and a workspace without it should render a shorter picker, not an
    // error page.
    api.slack.listUsers().then(setSlackUsers).catch(() => setSlackUsers([]));
    // Upcoming meetings, for the calendar trigger picker. Swallowed like the other two: a deployment
    // with no calendar connected should render a picker that says so, not an error page.
    api.calendar.meetings().then(setMeetings).catch(() => setMeetings([]));
    // Same reasoning as channels: an unconfigured credential is a state the editor renders as a
    // note about the server, not as an error.
    api.google
      .getStatus()
      .then(setGoogle)
      .catch(() => setGoogle(null));
  }, []);

  /** Any run still moving, anywhere on the page — the reason to keep polling. */
  const hasLiveRun = useMemo(() => {
    const inList = automations.some((a) => a.runs.some((r) => LIVE_STATUSES.includes(r.status)));
    const inExpanded = Object.values(runsFor).some((runs) => runs.some((r) => LIVE_STATUSES.includes(r.status)));
    return inList || inExpanded || (openRun !== null && LIVE_STATUSES.includes(openRun.status));
  }, [automations, runsFor, openRun]);

  // One interval for the whole page rather than one per live run: a burst of Slack-triggered runs
  // would otherwise mean a dozen timers all hitting the same endpoints.
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasLiveRun) {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = window.setInterval(() => {
      load();
      if (expanded) refreshRuns(expanded);
      if (openRunRef) refreshRun(openRunRef);
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLiveRun, expanded, openRunRef, load]);

  async function refreshRuns(automationId: string) {
    const runs = await api.orgAutomations.runs(automationId, { limit: 25 }).catch(() => null);
    if (runs) setRunsFor((prev) => ({ ...prev, [automationId]: runs }));
  }

  // A run belongs to the project its automation is homed at, which is where the detail route
  // lives — the run row is project-scoped data even though management of automations is not.
  async function refreshRun(ref: { id: string; projectId: string }) {
    const detail = await api.orgAutomations.run(ref.projectId, ref.id).catch(() => null);
    if (detail) setOpenRun(detail);
  }

  async function toggleExpanded(automationId: string) {
    const next = expanded === automationId ? null : automationId;
    setExpanded(next);
    if (next && !runsFor[next]) await refreshRuns(next);
  }

  /**
   * Puts the open run in the URL, and takes it out again when the drawer closes.
   *
   * Both ids, because run detail is served from the owning project's route and a run reached from
   * this org-wide list may belong to any project — `?run=` alone is not enough to fetch one. The
   * companion is still worth carrying: a pasted link then reopens the exact run without the page
   * having to guess, and `replace` keeps opening and closing runs out of the back-button history.
   */
  const putRunInUrl = useCallback(
    (ref: { id: string; projectId: string } | null) => {
      const next = new URLSearchParams(searchParams);
      if (ref) {
        next.set('run', ref.id);
        next.set('runProject', ref.projectId);
      } else {
        next.delete('run');
        next.delete('runProject');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  async function openRunDetail(run: { id: string; projectId: string }) {
    const ref = { id: run.id, projectId: run.projectId };
    setOpenRunRef(ref);
    setOpenRun(null);
    setRunLoading(true);
    putRunInUrl(ref);
    await refreshRun(ref);
    setRunLoading(false);
  }

  function closeRunDetail() {
    setOpenRunRef(null);
    setOpenRun(null);
    putRunInUrl(null);
  }

  async function save(draft: AutomationInput) {
    setSaving(true);
    setEditorError(null);
    try {
      // Empty strings are meaningful on a PATCH (they clear the column) but noise on a create,
      // so the payload is normalized here rather than in every branch of the editor.
      //
      // The scheduling fields the chosen kind doesn't use are dropped rather than nulled: an
      // omitted field leaves the column alone, so a weekly automation switched to monthly and back
      // still has the weekdays that were picked.
      const definition = automationTriggerDefinition(draft.trigger);
      const payload: AutomationInput = {
        ...draft,
        description: draft.description?.trim() || undefined,
        sourceFilter: draft.sourceFilter?.trim() || undefined,
        slackDestinations: draft.slackDestinations ?? [],
        driveFolderId: draft.driveFolderId?.trim() || undefined,
        timeOfDay: definition.needsTime ? draft.timeOfDay : undefined,
        daysOfWeek: definition.needsDaysOfWeek ? draft.daysOfWeek : undefined,
        dayOfMonth: definition.needsDayOfMonth ? draft.dayOfMonth : undefined,
        runDate: definition.needsRunDate ? draft.runDate : undefined,
      };

      if (editing) await api.orgAutomations.update(editing.id, { ...payload, projectId: homeProjectId });
      else await api.orgAutomations.create({ ...payload, projectId: homeProjectId });

      setEditorOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      setEditorError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function runNow(automation: Automation) {
    setBusyId(automation.id);
    try {
      const run = await api.orgAutomations.runNow(automation.id);
      await load();
      if (expanded === automation.id) await refreshRuns(automation.id);
      // Straight into the run so the output appears where the user is already looking.
      await openRunDetail(run);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function setEnabled(automation: Automation, enabled: boolean) {
    // Optimistic: the toggle is the one control where a round-trip's delay reads as a bug.
    setAutomations((prev) => prev.map((a) => (a.id === automation.id ? { ...a, enabled } : a)));
    try {
      await api.orgAutomations.update(automation.id, { enabled });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      await load();
    }
  }

  async function remove(automation: Automation) {
    if (!window.confirm(`Delete "${automation.name}" and its run history?`)) return;
    setBusyId(automation.id);
    try {
      await api.orgAutomations.remove(automation.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Layout
      title="Automations"
      action={
        <Button
          variant="primary"
          onClick={() => {
            setEditing(null);
            // A new automation defaults to the organization: the broadest home, and the one that
            // needs no decision from someone who just wants a digest.
            setHomeProjectId(organization?.id ?? allProjects[0]?.id ?? '');
            setEditorError(null);
            setEditorOpen(true);
          }}
        >
          <Plus size={14} />
          New automation
        </Button>
      }
    >
      {error && (
        <div className="mb-5 px-4 py-3 rounded-md bg-danger-bg border border-danger-ring text-sm text-danger-text flex items-start gap-2">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-danger-text/70 hover:text-danger-text">
            <X size={14} />
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-center text-ink-faint py-14">Loading…</p>
      ) : automations.length === 0 ? (
        <Card className="px-6 py-14 text-center">
          <Bot size={22} className="mx-auto text-ink-faint mb-3" />
          <p className="text-sm font-medium text-ink-secondary mb-1">No automations yet</p>
          <p className="text-xs text-ink-faint max-w-md mx-auto leading-relaxed">
            An automation is a prompt plus a trigger. Start from a template — a pre-meeting brief or a
            post-meeting summary — and edit the prompt until the output is the document you want.
          </p>
        </Card>
      ) : (
        <>
          <AutomationFilterBar filters={filters} onChange={setFilters} automations={automations} />
          {visible.length === 0 ? (
            <Card className="px-6 py-10 text-center">
              <p className="text-sm font-medium text-ink-secondary mb-1">
                {filters.show === 'toMe' && !automations.some((a) => a.deliveredToMe)
                  ? 'Nothing is delivered to you in Slack'
                  : 'No automations match these filters'}
              </p>
              <p className="text-xs text-ink-faint max-w-md mx-auto leading-relaxed">
                {filters.show === 'toMe' && !automations.some((a) => a.deliveredToMe)
                  ? 'An automation counts here when it sends its document to you as a direct message. Ones posting to a channel you are in are not counted.'
                  : 'Try another tab or clear the filters.'}
              </p>
              <Button size="sm" className="mt-4" onClick={() => setFilters({ ...DEFAULT_FILTERS, show: 'all' })}>
                Show all automations
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {visible.map((automation) => {
                const Icon = triggerIcon(automation.trigger);
                const lastRun = automation.runs[0];
                const isOpen = expanded === automation.id;
                const runs = runsFor[automation.id] ?? automation.runs;

                return (
                  <Card key={automation.id} className="overflow-hidden">
                    <div className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          <Toggle
                            checked={automation.enabled}
                            onChange={(next) => setEnabled(automation, next)}
                            label={`${automation.enabled ? 'Disable' : 'Enable'} ${automation.name}`}
                          />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className={cn('text-sm font-semibold truncate', automation.enabled ? 'text-ink' : 'text-ink-faint')}>
                              {automation.name}
                            </p>
                            {!automation.enabled && <Badge variant="muted">Paused</Badge>}
                            {/* Which project it is homed at. In a list spanning the organization the
                                name alone doesn't say where its output lands. */}
                            {automation.projectName && (
                              <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-ink-muted">
                                {automation.projectName}
                              </span>
                            )}
                            {automation.deliveredToMe && (
                              <span
                                className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300"
                                title="Sends its document to you as a Slack direct message"
                              >
                                <Send size={9} />
                                DMs you
                              </span>
                            )}
                          </div>

                          {automation.description && (
                            <p className="text-xs text-ink-muted mt-0.5 truncate">{automation.description}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-ink-faint">
                            <span className="inline-flex items-center gap-1">
                              <Icon size={11} />
                              {triggerSummary(automation)}
                            </span>
                            {(automation.createdByMe || automation.createdByName) && (
                              <span className="inline-flex items-center gap-1">
                                <UserRound size={10} />
                                {automation.createdByMe ? 'Created by you' : `By ${automation.createdByName}`}
                              </span>
                            )}
                            {automation.postToSlack && (
                              <span className="inline-flex items-center gap-1">
                                <Send size={10} />
                                {automation.slackFormat === 'HEADS_UP' && automation.saveToDrive ? 'Slack heads-up' : 'Posts to Slack'}
                              </span>
                            )}
                            {automation.saveToDrive && (
                              <span className="inline-flex items-center gap-1">
                                <FileText size={10} />
                                Files to Drive
                              </span>
                            )}
                            {lastRun && (
                              <span className="inline-flex items-center gap-1">
                                <Clock size={10} />
                                Last run {timeAgo(lastRun.queuedAt)}
                              </span>
                            )}
                            {/* Only while enabled: a paused automation has no next run, and saying one
                                would be the wrong half of the truth. */}
                            {automation.enabled && nextRunSummary(automation) && (
                              <span className="inline-flex items-center gap-1">
                                <CalendarClock size={10} />
                                Next {nextRunSummary(automation)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {/* A run in flight gets the breathing indicator and its current step rather than
                              a status word: on a page whose whole subject is agents doing work on a
                              schedule, "is anything happening right now" is the first question. */}
                          {lastRun && LIVE_STATUSES.includes(lastRun.status) ? (
                            <span
                              className="inline-flex items-center gap-1.5 max-w-[15rem]"
                              title={lastRun.activity ?? undefined}
                            >
                              <BreathingDot size={7} />
                              <span className="text-[11px] text-orange-600 dark:text-orange-400 truncate">
                                {lastRun.activity ?? (lastRun.status === 'QUEUED' ? 'queued' : 'running')}
                              </span>
                            </span>
                          ) : (
                            lastRun && <Badge variant={statusVariant(lastRun.status)}>{lastRun.status}</Badge>
                          )}
                          <Button size="sm" onClick={() => runNow(automation)} disabled={busyId === automation.id}>
                            {busyId === automation.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Play size={12} />
                            )}
                            Run now
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditing(automation);
                              setHomeProjectId(automation.projectId);
                              setEditorError(null);
                              setEditorOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <button
                            onClick={() => remove(automation)}
                            title="Delete automation"
                            className="p-1.5 rounded-md text-ink-faint hover:text-danger-text hover:bg-danger-bg transition-colors"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      <button
                        onClick={() => toggleExpanded(automation.id)}
                        className="mt-2 flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink-muted transition-colors"
                      >
                        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        Run history
                      </button>
                    </div>

                    {isOpen && (
                      <div className="border-t border-border-subtle divide-y divide-border-subtle bg-surface-subtle/40">
                        <AutomationStats automation={automation} lastRun={lastRun} />
                        {automation.saveToDrive && <DocEditTrend automationId={automation.id} />}
                        {runs.length === 0 ? (
                          <p className="px-4 py-4 text-xs text-ink-faint text-center">
                            No runs yet — press Run now to try the prompt.
                          </p>
                        ) : (
                          runs.map((run) => <RunRow key={run.id} run={run} onOpen={() => openRunDetail(run)} />)
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <AutomationEditor
        open={editorOpen}
        editing={editing}
        projects={allProjects}
        homeProjectId={homeProjectId}
        onHomeProjectChange={setHomeProjectId}
        meetings={meetings}
        slackUsers={slackUsers}
        templates={templates}
        channels={channels}
        google={google}
        saving={saving}
        error={editorError}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />

      <RunDrawer
        open={openRunRef !== null}
        run={openRun}
        loading={runLoading}
        onClose={closeRunDetail}
      />
    </Layout>
  );
}
